import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function LinkTelegram() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) setToken(t);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  if (!user) {
    // Redirect to login, preserving the link URL so the user returns after login
    navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`, { replace: true });
    return null;
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="max-w-sm text-center">
          <p className="text-gray-700">No token found in the link. Generate a new one from Settings.</p>
          <button
            onClick={() => navigate('/settings')}
            className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  async function copyToken() {
    if (!token) return;
    await navigator.clipboard.writeText(`/link ${token}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          <span className="text-4xl">🔗</span>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">Link Telegram</h1>
          <p className="mt-1 text-sm text-gray-500">
            Logged in as <span className="font-medium">{user.displayName}</span>
          </p>
        </div>

        <ol className="mb-6 space-y-4 text-sm text-gray-700">
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">1</span>
            <span>Open our Telegram bot and start a conversation.</span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">2</span>
            <span>
              Send this command to the bot (tap to copy):
              <button
                onClick={() => void copyToken()}
                className="mt-2 flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm hover:bg-gray-100"
              >
                <code className="text-left break-all text-gray-800">/link {token}</code>
                <span className="shrink-0 text-xs text-primary-600">{copied ? '✓ Copied' : 'Copy'}</span>
              </button>
            </span>
          </li>
          <li className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">3</span>
            <span>The bot will confirm your account is linked.</span>
          </li>
        </ol>

        <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
          ⏱ This token expires in 10 minutes.
        </p>

        <button
          onClick={() => navigate('/settings')}
          className="w-full rounded-lg border border-gray-300 py-2 text-sm font-medium hover:bg-gray-50"
        >
          Back to Settings
        </button>
      </div>
    </div>
  );
}
